import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  Divider,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { UploadOutlined } from "@ant-design/icons";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import toast from "react-hot-toast";
import axios from "axios";
import { URL } from "../config";

const AddTechnicianModal = ({ onClose, technician, onSaveComplete }) => {
  const isEditMode = !!technician;

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [profilePhoto, setProfilePhoto] = useState(null);
  const [aadharImg, setAadharImg] = useState(null);
  const [otherImg, setOtherImg] = useState(null);

  const [profilePreview, setProfilePreview] = useState("");
  const [aadharPreview, setAadharPreview] = useState("");
  const [otherPreview, setOtherPreview] = useState("");

  const [formData, setFormData] = useState({
    technicianName: "",
    mobileNo: "",
    alternateMobileNo: "",
    password: "",
    address: "",
  });

  const [errors, setErrors] = useState({
    mobileNo: "",
    alternateMobileNo: "",
  });

  const technicianNameRef = useRef();
  const mobileNoRef = useRef();
  const alternateMobileRef = useRef();
  const passwordRef = useRef();
  const addressRef = useRef();

  const fieldStyle = {
    "& .MuiOutlinedInput-root": {
      minHeight: "28px",
      fontSize: "14px",
      "& fieldset": { borderColor: "#c4c4c4" },
      "&:hover fieldset": { borderColor: "#184459ff" },
      "&.Mui-focused fieldset": {
        borderColor: "#c4c4c4",
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "18px",
      color: "#184459ff",
      fontWeight: 600,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#184459ff",
    },
  };

 
  useEffect(() => {
    if (isEditMode) {
      setFormData({
        technicianName: technician.technicianName || "",
        mobileNo: technician.mobileNo?.toString() || "",
        alternateMobileNo: technician.alternateMobileNo?.toString() || "",
        password: technician.password || "",
        address: technician.address || "",
      });

      setProfilePreview(technician.profilePhoto || "");
      setAadharPreview(technician.aadharImg || "");
      setOtherPreview(technician.otherImg || "");
    }
  }, [technician, isEditMode]);


  const handleChange = (field, value) => {
    if (field === "mobileNo" || field === "alternateMobileNo") {
      value = value.replace(/\D/g, "");
      if (value.length > 10) return;
    }
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: "" });
  };

  const handleBlur = (field) => {
    if (
      (field === "mobileNo" || field === "alternateMobileNo") &&
      formData[field] &&
      formData[field].length !== 10
    ) {
      setErrors({ ...errors, [field]: "Must be 10 digits" });
    }
  };

  const imageValidation = (file) => {
    if (!file?.type.startsWith("image/")) {
      toast.error("Only image allowed!");
      return false;
    }
    return true;
  };

  const handleProfilePhoto = (e) => {
    const file = e.target.files[0];
    if (!imageValidation(file)) return;
    setProfilePhoto(file);
    setProfilePreview(window.URL.createObjectURL(file));
  };

  const handleAadharImg = (e) => {
    const file = e.target.files[0];
    if (!imageValidation(file)) return;
    setAadharImg(file);
    setAadharPreview(window.URL.createObjectURL(file));
  };

  const handleOtherImg = (e) => {
    const file = e.target.files[0];
    if (!imageValidation(file)) return;
    setOtherImg(file);
    setOtherPreview(window.URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (
      !formData.technicianName ||
      !formData.mobileNo ||
      (!formData.password && !isEditMode) ||
      (!aadharImg && !aadharPreview)
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    if (
      formData.mobileNo.length !== 10 ||
      (formData.alternateMobileNo &&
        formData.alternateMobileNo.length !== 10)
    ) {
      toast.error("Mobile numbers must be 10 digits");
      return;
    }

    setLoading(true);

    try {
      const filePayload = new FormData();
      filePayload.append("technicianName", formData.technicianName);
      filePayload.append("mobileNo", formData.mobileNo);
      if (formData.alternateMobileNo)
        filePayload.append(
          "alternateMobileNo",
          formData.alternateMobileNo
        );
      filePayload.append("address", formData.address);
      filePayload.append("password", formData.password);

      if (profilePhoto) filePayload.append("profilePhoto", profilePhoto);
      if (aadharImg) filePayload.append("aadharImg", aadharImg);
      if (otherImg) filePayload.append("otherImg", otherImg);

      let response;
      if (isEditMode) {
        response = await axios.patch(
          `${URL}/technicians/update-with-files/${technician.technicianId}`,
          filePayload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success("Technician updated successfully!");
      } else {
        response = await axios.post(`${URL}/technicians`, filePayload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Technician added successfully!");
      }

      const updatedTech = {
        technicianId: isEditMode
          ? technician.technicianId
          : response.data.technicianId,
        technicianName: formData.technicianName,
        mobileNo: formData.mobileNo,
        alternateMobileNo: formData.alternateMobileNo,
        password: formData.password,
        address: formData.address,

        profilePhoto: response.data.profilePhoto
          ? `${URL}${response.data.profilePhoto}?t=${Date.now()}`
          : null,

        aadharImg: response.data.aadharImg
          ? `${URL}${response.data.aadharImg}?t=${Date.now()}`
          : null,

        otherImg: response.data.otherImg
          ? `${URL}${response.data.otherImg}?t=${Date.now()}`
          : null,
      };

      if (onSaveComplete) onSaveComplete(updatedTech);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save technician");
    }

    setLoading(false);
  };


  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: { width: { xs: "90%", sm: 450 }, borderRadius: 2, maxHeight: "90vh" },
      }}
      BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.4)" } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
       
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" textAlign="center" sx={{ color: "#184459ff" }}>
            {isEditMode ? "Edit Technician" : "Add Technician"}
          </Typography>
          <Divider sx={{ mt: 1 }} />
        </Box>

    
        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Technician Name *"
              size="small"
              fullWidth
              value={formData.technicianName}
              onChange={(e) => handleChange("technicianName", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldStyle}
              inputRef={technicianNameRef}
            />

            <TextField
              label="Mobile No *"
              size="small"
              fullWidth
              value={formData.mobileNo}
              onChange={(e) => handleChange("mobileNo", e.target.value)}
              onBlur={() => handleBlur("mobileNo")}
              error={!!errors.mobileNo}
              helperText={errors.mobileNo}
              InputLabelProps={{ shrink: true }}
              sx={fieldStyle}
              inputRef={mobileNoRef}
            />

            <TextField
              label="Alternate Mobile"
              size="small"
              fullWidth
              value={formData.alternateMobileNo}
              onChange={(e) => handleChange("alternateMobileNo", e.target.value)}
              onBlur={() => handleBlur("alternateMobileNo")}
              error={!!errors.alternateMobileNo}
              helperText={errors.alternateMobileNo}
              InputLabelProps={{ shrink: true }}
              sx={fieldStyle}
              inputRef={alternateMobileRef}
            />

          <TextField
  label="Password"
  size="small"
  fullWidth
  value={formData.password}
  onChange={(e) => handleChange("password", e.target.value)}
  InputLabelProps={{ shrink: true }}
  sx={fieldStyle}
  inputRef={passwordRef}
/>


            <TextField
              label="Address"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={formData.address}
              onChange={(e) => handleChange("address", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldStyle}
              inputRef={addressRef}
            />

         
            <UploadBox label="Upload Profile Photo" onChange={handleProfilePhoto} preview={profilePreview} />
            <UploadBox label="Upload Aadhar *" onChange={handleAadharImg} preview={aadharPreview} />
            <UploadBox label="Upload Other Image" onChange={handleOtherImg} preview={otherPreview} />
          </Stack>
        </Box>

   
        <Box sx={{ p: 3, borderTop: "1px solid #c4c4c4" }}>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#184459ff" }}
              onClick={handleSave}
              startIcon={loading && <CircularProgress size={18} />}
            >
              {loading ? "Saving..." : isEditMode ? "Update Technician" : "Add Technician"}
            </Button>
            <Button variant="contained" sx={{ backgroundColor: "#757575ff" }} onClick={onClose}>
              Close
            </Button>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
};


const UploadBox = ({ label, onChange, preview }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      border: "1px solid #c4c4c4",
      p: 1.5,
      borderRadius: 2,
    }}
  >
    <Button component="label" startIcon={<UploadOutlined />}>
      {label}
      <input hidden type="file" accept="image/*" onChange={onChange} />
    </Button>
    {preview && <img src={preview} alt="preview" style={{ width: 50, height: 50, borderRadius: 8 }} />}
  </Box>
);

export default AddTechnicianModal;
